import { useMemo, useState, type ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Breadcrumbs } from '../../components/shell/index.js';
import { Badge, buttonClassName } from '../../components/primitives/index.js';
import { ErrorState, LoadingState, Skeleton } from '../../components/feedback/index.js';
import {
  AvailabilityView,
  ModelSummary,
  RateCardView,
  formatRange,
  modelLine,
} from '../../components/discovery/index.js';
import { fetchModel } from '../../lib/api/queries/catalog.js';
import {
  fetchAvailability,
  fetchEquipment,
  fetchRateCard,
} from '../../lib/api/queries/equipment.js';
import { availabilityAnswer, headlineRate } from '../../lib/api/discovery.js';
import { currencyLabel, formatMinorUnits } from '../../lib/format/money.js';
import { formatInterval } from '../../lib/format/interval.js';
import { ApiError } from '../../lib/api/errors.js';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { MotifField, MotifRule } from '../../components/brand/index.js';
import { Icon, machineMotif } from '../../components/icons/index.js';

/**
 * `/equipment/:id` — P-Q012, with P-Q006, P-Q013 and P-Q014 beside it.
 *
 * This is the most important customer screen in the product, and it used to
 * read as a record: a heading over a fact list, the rate below the fold, the
 * calendar below that, and the only action at the top where it scrolled away.
 * The page now answers, in order, the four questions somebody deciding on a
 * machine actually asks —
 *
 *   1. **what is it** — the hero: type, title, model, year
 *   2. **can I have it on my dates** — the answer as a sentence, with the dates
 *      editable right there
 *   3. **what does it cost** — one published rate, promoted and typeset
 *   4. **what do I do next** — one action, which does not scroll away
 *
 * — and only then the full record beneath, which is what it always showed.
 *
 * Evidence-first still means the page is silent where the record is silent.
 * What it deliberately does not show:
 *
 *   · **the owner.** L0 carries no owner identity, and P-Q012 strips it for
 *     anyone but the owner themselves.
 *   · **a location.** No serialiser exposes one at any public level.
 *   · **photographs.** `asset.unit_media` has no public read endpoint, so the
 *     hero is geometry and type — never a placeholder image implying one is
 *     coming.
 *   · **documents.** P-Q017 is owner/admin only and visibility-filtered.
 *
 * Each of those is absent because the API withholds it, and the page says so
 * plainly rather than leaving a reader to assume the machine has no
 * photographs or no papers.
 */

const DAY_MS = 86_400_000;

/**
 * A catalogue code as a phrase a person reads.
 *
 * `operator_required` becomes "Operator required", not "Operator Required":
 * title case on a machine attribute reads as a proper noun, and these are not
 * proper nouns. Done here rather than in CSS, because `text-transform:
 * capitalize` capitalises every word and cannot know which is the first.
 */
function phrase(code: string): string {
  const words = code.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** `YYYY-MM-DD`, which is what a date control speaks. */
function isoDate(offsetDays: number): string {
  return new Date(Date.now() + offsetDays * DAY_MS).toISOString().slice(0, 10);
}

export function EquipmentDetailPage(): ReactElement {
  const { id = '' } = useParams<{ id: string }>();
  const { account, has } = useAuth();

  /**
   * P-Q013 requires an explicit window, and the window is the CALLER's choice —
   * so it is the reader's.
   *
   * The default is a FORTNIGHT from tomorrow, not the two-month look-ahead this
   * page used to ask for. Two months is the right window for reading a
   * calendar and the wrong one for answering "can I have it": over sixty days
   * almost every machine has something blocked somewhere, so the answer was
   * "not free for all of these dates" on essentially every unit — true, and
   * useless. A fortnight is the shape of a decision, and the reader can widen
   * it in one control.
   */
  const [from, setFrom] = useState(() => isoDate(1));
  const [to, setTo] = useState(() => isoDate(14));

  const window = useMemo(
    () => ({ from: `${from}T00:00:00.000Z`, to: `${to}T23:59:59.999Z` }),
    [from, to],
  );

  const unit = useQuery({
    queryKey: ['equipment', id],
    queryFn: () => fetchEquipment(id),
    enabled: id !== '',
  });

  const model = useQuery({
    queryKey: ['model', unit.data?.model_id],
    queryFn: () => fetchModel(unit.data?.model_id ?? ''),
    enabled: unit.data?.model_id !== undefined,
  });

  const availability = useQuery({
    queryKey: ['availability', id, window.from, window.to],
    queryFn: () => fetchAvailability(id, window.from, window.to),
    enabled: unit.isSuccess,
  });

  const rateCard = useQuery({
    queryKey: ['rate-card', id],
    queryFn: () => fetchRateCard(id),
    enabled: unit.isSuccess,
  });

  if (unit.isLoading) {
    return (
      <div className="detail-page">
        <LoadingState rows={5} label="Loading this machine" />
      </div>
    );
  }

  if (unit.isError || unit.data === undefined) {
    return (
      <div className="detail-page">
        <ErrorState error={unit.error} onRetry={() => void unit.refetch()} />
      </div>
    );
  }

  const heading = modelLine(model.data);
  const answer = availability.isSuccess ? availabilityAnswer(availability.data, from, to) : null;
  const rate = rateCard.isSuccess ? headlineRate(rateCard.data) : null;
  // Two at most: a list of every gap in a fortnight is a calendar, and the
  // calendar is already below.
  const freeWindows = (availability.data?.free ?? []).slice(0, 2);
  const rateMissing =
    rateCard.isError && rateCard.error instanceof ApiError && rateCard.error.status === 404;

  // P-C027 permits `request-to-book` for a customer against a published unit.
  // Anyone else is offered the step that actually exists for them: signing in.
  const canRequest = account !== null && has('customer');
  const actionLabel = canRequest ? 'Request terms' : 'Sign in to request terms';
  const actionHref = canRequest ? `/app/equipment/${unit.data.id}/request` : '/login';

  return (
    <div className="detail-page">
      <Breadcrumbs
        items={[{ label: 'Equipment', to: '/equipment' }, { label: unit.data.display_title }]}
      />

      {/*
        The hero, in the position a photograph would take.

        There is no photograph and there will not be one — §11.2's anonymous
        view is five fields and the repository ships no imagery — so the weight
        comes from the machine's TYPE DRAWING and from type: the title set
        large, the catalogue line beneath it, the build year stamped beside
        them. Every value on it came from the API.
      */}
      <header className="machine-hero">
        <MotifField height={260} />
        <span className="machine-hero__plate" aria-hidden="true">
          <Icon name={machineMotif(unit.data.display_title)} size="lg" />
        </span>
        <div className="machine-hero__copy">
          <h1 className="machine-hero__title">{unit.data.display_title}</h1>
          {heading === null ? null : <p className="machine-hero__model">{heading}</p>}
          <MotifRule width={40} />
          {/*
            The identity strip. Each fact is ONE text run rather than a
            term/value pair, so the year is never a lone node duplicating the
            one the record below already carries — a page that prints the same
            fact twice as two separate things reads as two facts.
          */}
          <ul className="machine-hero__facts">
            {unit.data.year_of_manufacture === null ? null : (
              <li className="machine-hero__fact tabular">Built {unit.data.year_of_manufacture}</li>
            )}
            {model.data?.operator_requirement === null ||
            model.data?.operator_requirement === undefined ? null : (
              <li className="machine-hero__fact">{phrase(model.data.operator_requirement)}</li>
            )}
            {model.data?.default_meter_type === null ||
            model.data?.default_meter_type === undefined ? null : (
              <li className="machine-hero__fact">
                Metered by {model.data.default_meter_type.replace(/_/g, ' ')}
              </li>
            )}
          </ul>
        </div>
      </header>

      {/*
        The decision panel — the two questions a customer has, answered before
        the record rather than beneath it, and the action beside them.
      */}
      <section className="decision" aria-labelledby="decision-heading">
        <h2 id="decision-heading" className="visually-hidden">
          Dates, rate and next step
        </h2>

        <div className="decision__dates">
          <p className="decision__label">Your dates</p>
          <div className="decision__fields">
            <label className="decision__field">
              <span>From</span>
              <input
                type="date"
                value={from}
                max={to}
                onChange={(event) => setFrom(event.target.value)}
              />
            </label>
            <label className="decision__field">
              <span>To</span>
              <input
                type="date"
                value={to}
                min={from}
                onChange={(event) => setTo(event.target.value)}
              />
            </label>
          </div>

          <p className="decision__answer" role="status" aria-live="polite">
            {availability.isLoading ? (
              <Skeleton height={20} width="60%" />
            ) : answer === null ? (
              <span className="decision__answer-pending">Availability could not be read.</span>
            ) : (
              <Badge tone={answer.tone === 'neutral' ? 'neutral' : answer.tone}>
                {answer.label}
              </Badge>
            )}
          </p>
          {/*
            The free windows, named. A badge that says "not free for all of
            these dates" tells a reader they cannot have what they asked for and
            nothing about what they CAN have; these are the owner's own free
            intervals inside the window they chose, printed verbatim.
          */}
          {freeWindows.length === 0 ? null : (
            <p className="decision__free">
              Free {freeWindows.map((window) => formatRange(window.start, window.end)).join(' · ')}
            </p>
          )}

          {/* BR-9.8 — sent as `note_key` on every response, and required to be
              shown. Answering the question does not soften it. */}
          <p className="decision__caveat">
            Availability is informational until a hold is created — nothing here reserves the
            machine.
          </p>
        </div>

        <div className="decision__money">
          <p className="decision__label">Published rate</p>
          {rateCard.isLoading ? (
            <Skeleton height={32} width="70%" />
          ) : rate !== null && rateCard.isSuccess ? (
            <>
              <p className="decision__amount">
                <span className="decision__currency">{currencyLabel(rateCard.data.currency)}</span>
                <span className="numeric decision__figure">
                  {formatMinorUnits(rate.rate_amount_minor, rateCard.data.currency)}
                </span>
                <span className="decision__per">
                  per {formatInterval(rate.period_length) ?? rate.period_type}
                </span>
              </p>
              {rate.included_usage_hours === null ? null : (
                <p className="decision__included numeric">{rate.included_usage_hours} h included</p>
              )}
              <p className="decision__caveat">
                The owner’s published day rate. They quote the final terms on your request.
              </p>
            </>
          ) : (
            <p className="decision__caveat">
              No rates are published for this machine. Ask for terms and the owner will quote you
              directly.
            </p>
          )}

          <Link to={actionHref} className={`decision__cta ${buttonClassName('primary', 'lg')}`}>
            {actionLabel}
          </Link>
        </div>
      </section>

      <section aria-labelledby="identity-heading">
        <h2 id="identity-heading" className="section-title">
          This machine
        </h2>
        <ModelSummary
          model={model.data}
          yearOfManufacture={unit.data.year_of_manufacture}
          specificationLayout="grid"
        />
        <p className="discovery-note">
          Photographs, documents and the machine’s location are not published to browsers. You see
          them at handover, when both parties record the machine’s condition together.
        </p>
      </section>

      {rateCard.isLoading ? <LoadingState rows={3} label="Loading rates" /> : null}
      {rateCard.isError ? (
        rateMissing ? (
          <section aria-labelledby="rates-missing">
            <h2 id="rates-missing" className="section-title">
              Rates
            </h2>
            <p className="detail-page__footnote">
              This owner has not published rates for this machine. Ask them for terms and they will
              quote you directly.
            </p>
          </section>
        ) : (
          <ErrorState error={rateCard.error} onRetry={() => void rateCard.refetch()} />
        )
      ) : null}
      {rateCard.isSuccess ? <RateCardView rateCard={rateCard.data} /> : null}

      {availability.isLoading ? <LoadingState rows={3} label="Loading availability" /> : null}
      {availability.isError ? (
        <ErrorState error={availability.error} onRetry={() => void availability.refetch()} />
      ) : null}
      {availability.isSuccess ? (
        <AvailabilityView availability={availability.data} from={window.from} to={window.to} />
      ) : null}

      {/*
        The action does not scroll away on a phone.

        Below `lg` this bar is fixed to the bottom of the viewport; the page
        reserves its height so the last section is reachable rather than sitting
        under it. On a wide screen the decision panel is still on screen beside
        the record, so the bar is not drawn at all.
      */}
      <div className="sticky-action" aria-hidden="true">
        <span className="sticky-action__machine">{unit.data.display_title}</span>
        <Link to={actionHref} tabIndex={-1} className={buttonClassName('primary', 'md')}>
          {actionLabel}
        </Link>
      </div>
    </div>
  );
}
